/**
 * Zod Schemas for LumaScript Validation
 */

import { z } from 'zod';

/**
 * Position Schema
 */
export const positionSchema = z.object({
  x: z.number(),
  y: z.number()
});

/**
 * Size Schema
 */
export const sizeSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive()
});

/**
 * Port Schema
 */
export const portSchema = z.object({
  id: z.string().uuid(),
  group: z.enum(['in', 'out'])
});

/**
 * Ports Schema
 */
export const portsSchema = z.object({
  items: z.array(portSchema)
});

/**
 * Luma Node Types (all 21+ types)
 */
export const lumaNodeTypeSchema = z.enum([
  // Utilities
  'luma.SetAttribute',
  'luma.ClearAttribute',
  'luma.SetKPI',
  'luma.CaptureSurvey',
  'luma.CaptureSurveyFeedback',
  'luma.ExecuteScript',
  'luma.End',
  // Generative AI
  'luma.AITask',
  'luma.AINlq',
  'luma.CategorizeUserIntent',
  // Skills
  'luma.SearchSkills',
  'luma.SwitchSkill',
  'luma.DisplaySkill',
  'luma.SuggestSkills',
  'luma.ExecuteSubFlow',
  // Knowledge
  'luma.SearchKnowledge',
  'luma.DisplayPreciseAnswer',
  'luma.DisplayKnowledge',
  'luma.CreateKnowledgeArtifact',
  // Catalog
  'luma.SearchCatalog',
  'luma.DisplayCatalog',
  // Prompts
  'luma.PromptAttribute',
  // Messages
  'luma.Message',
  'luma.DecisionPrompt',
  'luma.ConfirmationPrompt',
  'luma.DatePrompt',
  // Control Flow
  'luma.Start',
  'luma.Condition',
  // Integrations (Phase 1: stub pattern - detected but not yet executable)
  'luma.Integration'
]);

/**
 * Cell Type Schema
 */
export const cellTypeSchema = z.enum([
  'START',
  'END',
  'MESSAGE',
  'AI_TASK',
  'AI_NLQ',
  'PROMPT',
  'CONDITION',
  'SEARCH',
  'DISPLAY',
  'SKILL',
  'KNOWLEDGE',
  'CATALOG',
  'SCRIPT',
  'ATTRIBUTE',
  'KPI',
  'SURVEY',
  'INTEGRATION'
]);

/**
 * Luma Node Schema
 */
export const lumaNodeSchema = z.object({
  id: z.string().uuid(),
  type: lumaNodeTypeSchema,
  cellType: cellTypeSchema,
  name: z.string().min(1),
  definition: z.record(z.any()).optional(),
  position: positionSchema,
  size: sizeSchema,
  ports: portsSchema.optional()
});

/**
 * Connection Point Schema
 */
export const connectionPointSchema = z.object({
  cell: z.string().uuid(),
  port: z.string().uuid()
});

/**
 * Connection Schema
 */
export const connectionSchema = z.object({
  id: z.string().uuid(),
  source: connectionPointSchema,
  target: connectionPointSchema
});

/**
 * Metadata Schema
 */
export const metadataSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  facets: z.array(z.string()),
  version: z.string().optional(),
  author: z.string().optional(),
  tags: z.array(z.string()).optional()
});

/**
 * LumaScript Schema
 */
export const lumaScriptSchema = z.object({
  version: z.literal('1.0'),
  metadata: metadataSchema,
  nodes: z.array(lumaNodeSchema).min(1),
  connections: z.array(connectionSchema)
});

/**
 * Export types from schemas
 */
export type LumaScriptInput = z.input<typeof lumaScriptSchema>;
export type LumaScriptOutput = z.output<typeof lumaScriptSchema>;
export type LumaNodeInput = z.input<typeof lumaNodeSchema>;
export type LumaNodeOutput = z.output<typeof lumaNodeSchema>;
